import { useEffect } from 'react';
import { verifyActiveConnection } from '../../api/connections';
import { startHistoryLogging } from '../../data/history';
import { loadHome } from '../../data/home';
import { initDownloads } from '../../downloads/manager';
import { initAudioFx } from '../../player/audioFx';
import { player } from '../../player/engine';
import { initPlaybackOutputs } from '../../player/output';
import { flushPendingProgress } from '../../player/progress';
import { reportError } from './errorReporter';
import { preloadGates, preloadScreens, whenIdle } from './screens';

const GOOGLE_BACKUP_ENABLED = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);

/**
 * Background work once signed in (`_ConnectedAppGate`, SHELL-005): nothing
 * blocks the UI; screens show their own skeletons.
 */
/** Background work never surfaces as an unhandled rejection; failures are logged (screens show their own errors). */
function background(task: () => Promise<unknown>, operation: string): void {
  try {
    task().catch((error: unknown) => reportError(error, operation, 'startup'));
  } catch (error) {
    reportError(error, operation, 'startup');
  }
}

export function SignedInEffects({ scope }: { scope: string }) {
  useEffect(() => {
    background(initDownloads, 'initDownloads');
    startHistoryLogging();
    initAudioFx();
    // Cast devices (Chrome) and AirPlay (Safari) for the player's cast button.
    initPlaybackOutputs();
    // The gates too (polish pass): a sign-out or an expired session shows onboarding without a blank wait.
    return whenIdle(() => {
      preloadScreens();
      preloadGates();
    });
  }, []);

  useEffect(() => {
    // Opens the book in the mini player paused, so Play starts without waiting on the server.
    player.preload();
    background(flushPendingProgress, 'flushPendingProgress');
    // Replaces the old background authorize(): refreshes progress and server version, handles deactivated connections.
    background(verifyActiveConnection, 'verifyActiveConnection');
    background(loadHome, 'loadHome');
    if (GOOGLE_BACKUP_ENABLED) {
      background(() => import('../../data/backup').then((m) => m.maybeAutoBackupToGoogle()), 'maybeAutoBackupToGoogle');
    }
  }, [scope]);

  return null;
}
